import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  Tooltip,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Calculate as CalculateIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import {
  LayoutDashboard,
  CandlestickChart,
  AlignLeft,
  Lightbulb,
  Pencil,
  Activity,
  ArrowLeftRight,
  PieChart,
  Cog,
  ChevronLeft,
  Globe2,
  Bug,
  Eye,
  ReceiptText,
  Telescope,
  NotebookPen,
  Inbox,
  CheckCircle2
} from 'lucide-react';
import {
  Check as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useDemo } from '../../contexts/DemoContext';
import { useEntity } from '../../contexts/EntityContext';
import EntityDialog from './EntityDialog';
import RenameEntityDialog from './RenameEntityDialog';
import { Chip } from '@mui/material';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import api from '../../services/api';
import needsYouService, { NEEDS_YOU_CHANGED } from '../../services/needsYouService';
import PositionCalculator from './PositionCalculator';
import HelpCenter from './HelpCenter';
import ChatAssistant from '../chat/ChatAssistant';
import { ChatDockProvider, useChatDock } from '../../contexts/ChatDockContext';
import ThemeToggle from './ThemeToggle';
import { KodahfyLogo, KodahfyBadge } from './KodahfyLogo';
import ReactCountryFlag from 'react-country-flag';
import { ChunkErrorBoundary, PageFallback } from '../../utils/lazyPage';

// Currency to country code mapping for flags
const CURRENCY_TO_COUNTRY = {
  CAD: 'CA',
  AUD: 'AU',
  USD: 'US',
  EUR: 'EU',
  GBP: 'GB',
  NZD: 'NZ',
  CHF: 'CH',
  JPY: 'JP',
  SGD: 'SG',
  HKD: 'HK',
  CNY: 'CN',
  INR: 'IN',
  KRW: 'KR',
  MXN: 'MX',
  BRL: 'BR',
  ZAR: 'ZA',
  SEK: 'SE',
  NOK: 'NO',
  DKK: 'DK',
  PLN: 'PL',
  THB: 'TH',
  MYR: 'MY',
  IDR: 'ID',
  PHP: 'PH',
  TWD: 'TW',
  AED: 'AE',
  SAR: 'SA',
  ILS: 'IL',
  TRY: 'TR',
  RUB: 'RU'
};

// Get country code for currency
const getCountryCode = (currency) => {
  return CURRENCY_TO_COUNTRY[currency] || 'UN';
};

// Icon styling configuration — light & dark bg variants
const iconStyles = {
  dashboard: { color: '#2563eb', bg: '#dbeafe', darkBg: 'rgba(37, 99, 235, 0.15)' },
  reconciliation: { color: '#f97316', bg: '#ffedd5', darkBg: 'rgba(249, 115, 22, 0.15)' },
  trades: { color: '#16a34a', bg: '#dcfce7', darkBg: 'rgba(22, 163, 74, 0.15)' },
  tradingPlan: { color: '#4f46e5', bg: '#e0e7ff', darkBg: 'rgba(79, 70, 229, 0.15)' },
  charts: { color: '#0ea5e9', bg: '#e0f2fe', darkBg: 'rgba(14, 165, 233, 0.15)' },
  research: { color: '#0ea5e9', bg: '#e0f2fe', darkBg: 'rgba(14, 165, 233, 0.15)' },
  notes: { color: '#0ea5e9', bg: '#e0f2fe', darkBg: 'rgba(14, 165, 233, 0.15)' },
  mindMarkets: { color: '#e11d48', bg: '#ffe4e6', darkBg: 'rgba(225, 29, 72, 0.15)' },
  journal: { color: '#f43f5e', bg: '#fff1f2', darkBg: 'rgba(244, 63, 94, 0.15)' },
  psychology: { color: '#f43f5e', bg: '#fff1f2', darkBg: 'rgba(244, 63, 94, 0.15)' },
  transactions: { color: '#9333ea', bg: '#f3e8ff', darkBg: 'rgba(147, 51, 234, 0.15)' },
  nav: { color: '#0891b2', bg: '#cffafe', darkBg: 'rgba(8, 145, 178, 0.15)' },
  tax: { color: '#ca8a04', bg: '#fef9c3', darkBg: 'rgba(202, 138, 4, 0.15)' },
  settings: { color: '#64748b', bg: '#f1f5f9', darkBg: 'rgba(100, 116, 139, 0.15)' }
};

// Helper to resolve bg based on current theme. `isDark` is passed in from the
// component (sourced from the reactive useThemeMode() context) rather than read
// from the DOM here — reading document.documentElement during render is stale on
// the toggle tick (App writes data-theme in a post-render effect), which left the
// logo and icon tints one toggle behind. See KodahfyLogo variant below.
const getIconBg = (styleKey, isDark) => {
  return isDark ? iconStyles[styleKey].darkBg : iconStyles[styleKey].bg;
};

// Styled icon wrapper component
// Colour budget (3 Oct 2026): the sidebar is grey line icons; the one
// accent marks the active item. The per-page colours above stay for the
// few places that still key off them; the sidebar no longer paints tiles.
const StyledIcon = ({ icon: Icon, styleKey, isActive, isSubItem, isDark }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: isSubItem ? 28 : 32,
        height: isSubItem ? 28 : 32,
        borderRadius: 1,
        transition: 'color 0.2s ease'
      }}
    >
      <Icon
        size={isSubItem ? 16 : 18}
        color={isActive ? 'var(--teal)' : 'var(--text-secondary)'}
        strokeWidth={1.75}
      />
    </Box>
  );
};

// A neuron, drawn in the icon set's own line style (JC, 3 Oct 2026): a cell
// body with dendrites branching out, for Mind & Markets. Same props as the
// lucide icons so StyledIcon treats it like one.
const NeuronIcon = ({ size = 18, color = 'currentColor', strokeWidth = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="2.6" />
    <path d="M13.8 10.2 16.5 7.3M16.5 7.3 15.6 4.8M16.5 7.3 19.2 6.6" />
    <path d="M14.4 12.6 18.4 13.4M18.4 13.4 20.6 11.9M18.4 13.4 19.8 15.8" />
    <path d="M12.6 14.5 13.6 18.6M13.6 18.6 11.4 20.6M13.6 18.6 16 20" />
    <path d="M9.9 13.4 6.1 15.2M6.1 15.2 3.6 14.3M6.1 15.2 5.4 18" />
    <path d="M10.2 10.1 7.6 7.2M7.6 7.2 4.8 7.6M7.6 7.2 8.2 4.4" />
  </svg>
);

// The board's settings glyph (JC, 3 Oct 2026): a small circle with eight
// spokes, lighter than the icon set's toothed gear at sidebar size.
const SettingsGlyph = ({ size = 18, color = 'currentColor', strokeWidth = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
  </svg>
);

const drawerWidthExpanded = 240;
const drawerWidthCollapsed = 72;
// KC docked panel width is resizable — the authoritative value lives in
// ChatDockContext (`dockedWidth`) and is mirrored to the CSS custom
// property `--kc-docked-width`. Layout-shift calcs below read the CSS var
// directly when docked so the main content follows the drag in real time
// without per-frame React re-renders.

const menuItems = [
  { text: 'Dashboard', icon: LayoutDashboard, styleKey: 'dashboard', path: '/dashboard' },
  // Needs you (17 Oct 2026): the night bookkeeper's questions and what it did,
  // with their count, and Reconciliation under the same icon, open by default
  // (JC: matching by hand was "way too hidden" for people with Auto-Match off).
  {
    text: 'Needs you',
    icon: Inbox,
    styleKey: 'reconciliation',
    countKey: 'needsYou',
    defaultOpen: true,
    children: [
      { text: 'Questions', title: 'Needs you', icon: Inbox, styleKey: 'reconciliation', path: '/needs-you', countKey: 'needsYou' },
      { text: 'Reconciliation', title: 'Reconciliation', icon: CheckCircle2, styleKey: 'reconciliation', path: '/reconciliation' }
    ]
  },
  { text: 'Trades', icon: CandlestickChart, styleKey: 'trades', path: '/trades' },
  { text: 'Trading Plan', icon: AlignLeft, styleKey: 'tradingPlan', path: '/trading-plan' },
  // Research (5 Oct 2026): the market, and the user's own notes about it.
  {
    text: 'Research',
    icon: Telescope,
    styleKey: 'research',
    children: [
      { text: 'Macro', icon: Globe2, styleKey: 'charts', path: '/macro' },
      { text: 'Notes', icon: NotebookPen, styleKey: 'notes', path: '/notes' }
    ]
  },
  {
    text: 'Mind & Markets',
    icon: NeuronIcon,
    styleKey: 'mindMarkets',
    children: [
      { text: 'Journal', icon: Pencil, styleKey: 'journal', path: '/mind-markets/journal' },
      { text: 'Psychology', icon: Activity, styleKey: 'psychology', path: '/mind-markets/psychology' }
    ]
  },
  { text: 'Transactions', icon: ArrowLeftRight, styleKey: 'transactions', path: '/transactions' },
  { text: 'Portfolio NAV', icon: PieChart, styleKey: 'nav', path: '/nav' },
  // Fund-admin cluster reading order: movements → value → what the taxman
  // sees. The live-FY position keeps this page alive outside June.
  { text: 'Tax', icon: ReceiptText, styleKey: 'tax', path: '/tax' },
  { text: 'Settings', icon: SettingsGlyph, styleKey: 'settings', path: '/settings' }
];

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { reportingCurrency } = useCurrency();
  const { isDemoMode, demoAvailable, demoDisplayName, enterDemoMode, exitDemoMode } = useDemo();
  // Item 82 — entity switcher state
  const { entities, activeEntityId, activeEntity, isEntityActive, setActiveEntity, refreshEntities } = useEntity();
  const [entityDialogOpen, setEntityDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null); // entity being renamed
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  // Logo wordmark/badge: white text on the dark sidebar, near-black on the light one.
  const logoVariant = isDark ? 'light' : 'dark';

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});

  // Collapsed-rail submenu flyout. When the sidebar is collapsed (manually,
  // or forced by the docked KC panel), a parent with children used to
  // navigate straight to its FIRST child — Psychology and every other
  // non-first subpage became unreachable while KC was docked (JC, 29 Aug).
  // Clicking a parent now opens this anchored menu of its children instead.
  // Placed by the clicked icon's position, not the element: the rail re-renders
  // on open and the element goes stale, which dropped the menu in the top-left
  // corner over the logo (JC, 5 Oct 2026).
  const [railFlyout, setRailFlyout] = useState(null); // { pos: { top, left }, item }
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Email verification banner state
  const [verificationBannerDismissed, setVerificationBannerDismissed] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const showVerificationBanner = !isDemoMode && user && user.email_verified === false && !verificationBannerDismissed;

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification');
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      // Silently fail — rate limit message will show in response
    } finally {
      setResendLoading(false);
    }
  };

  // Sidebar collapsed state - persisted to localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('nvst_sidebar_collapsed');
    return saved === 'true';
  });

  // Toggle sidebar collapse (no-op when chat is docked — sidebar forced collapsed)
  const toggleSidebar = () => {
    if (isChatDocked) return;
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('nvst_sidebar_collapsed', String(newState));
  };

  // Chat dock state — when docked, force sidebar collapsed
  // Safe to call here because Layout is always rendered inside ChatDockProvider (see bottom of this file)
  const { isDocked: isChatDocked, dockedWidth } = useChatDock();
  const effectiveSidebarCollapsed = isChatDocked ? true : sidebarCollapsed;
  const drawerWidth = effectiveSidebarCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;
  // Reusable template for layout-shift widths when docked: reads the CSS var
  // so the main content shrinks in sync with drag. `dockedWidth` is the
  // same-framing fallback used before the custom property is first set.
  const dockedWidthVar = `var(--kc-docked-width, ${dockedWidth}px)`;

  // Global keyboard shortcut handler (Ctrl+K / Cmd+K)
  const handleKeyDown = useCallback((event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      setCalculatorOpen(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // A group opens on its own children's pages, and by default when it says so;
  // once the arrow is pressed, the arrow decides (it folds even on a child's page).
  const isGroupOpen = (item) => expandedMenus[item.text] ?? (!!item.defaultOpen || isChildActive(item.children));
  const handleMenuExpand = (item) => {
    const open = isGroupOpen(item);
    setExpandedMenus(prev => ({ ...prev, [item.text]: !open }));
  };

  // A top-level entry is active on its own path and on the pages behind it.
  const isItemActive = (item) => location.pathname === item.path || (item.alsoActive || []).some(p => location.pathname.startsWith(p));

  // The Needs-you count on the menu (17 Oct 2026): re-read on every page
  // change and whenever an answer is given anywhere.
  const [needsYouCount, setNeedsYouCount] = useState(0);
  useEffect(() => {
    if (isDemoMode || !user) { setNeedsYouCount(0); return undefined; }
    let alive = true;
    const load = () => needsYouService.summary().then(s => { if (alive) setNeedsYouCount(s?.count || 0); }).catch(() => {});
    load();
    window.addEventListener(NEEDS_YOU_CHANGED, load);
    return () => { alive = false; window.removeEventListener(NEEDS_YOU_CHANGED, load); };
  }, [location.pathname, isDemoMode, user]);
  const countFor = (item) => (item.countKey === 'needsYou' ? needsYouCount : 0);

  // The one amber on the menu: a state that needs the person (design A).
  const CountBadge = ({ n, dot = false }) => (
    <Box component="span" aria-label={`${n} need you`} sx={dot
      ? { position: 'absolute', top: 6, right: 10, width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--orange, #D97706)' }
      : { ml: 1, minWidth: 20, height: 20, px: 0.75, borderRadius: 10, bgcolor: 'var(--orange-bg, #FEF3C7)', color: 'var(--orange, #B45309)', fontSize: '0.75rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}>
      {dot ? null : n}
    </Box>
  );

  // Check if any child path is active
  const isChildActive = (children) => {
    return children?.some(child => location.pathname.startsWith(child.path));
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    exitDemoMode();
    await logout();
    navigate('/login');
  };

  // Use effectiveSidebarCollapsed for drawer rendering (respects dock state)
  const isCollapsed = effectiveSidebarCollapsed;

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar
        sx={{
          cursor: 'pointer',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          minHeight: '64px !important',
          px: isCollapsed ? 1 : 2,
          '&:hover': { bgcolor: 'var(--bg-sidebar-active)' }
        }}
        onClick={toggleSidebar}
      >
        {isCollapsed ? (
          <Tooltip title="Expand sidebar" placement="right">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <KodahfyBadge
                size={36}
                variant={logoVariant}
              />
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <KodahfyLogo
              height={32}
              variant={logoVariant}
            />
            <Tooltip title="Collapse sidebar">
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <ChevronLeft size={20} />
              </Box>
            </Tooltip>
          </Box>
        )}
      </Toolbar>
      <Divider />
      <List sx={{ px: isCollapsed ? 0.5 : 1, flexGrow: 1, color: 'var(--text-secondary)' }}>
        {menuItems.map((item) => {
          // Wrapper for tooltip when collapsed
          const MenuItemWrapper = ({ children, title }) => {
            if (isCollapsed) {
              return (
                <Tooltip title={title} placement="right" arrow>
                  {children}
                </Tooltip>
              );
            }
            return children;
          };

          return (
            <React.Fragment key={item.text}>
              {item.children ? (
                <>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <MenuItemWrapper title={item.text}>
                      <ListItemButton
                        onClick={(e) => {
                          if (!isCollapsed) { handleMenuExpand(item); return; }
                          const r = e.currentTarget.getBoundingClientRect();
                          setRailFlyout({ pos: { top: r.top, left: r.right + 8 }, item });
                        }}
                        sx={{
                          borderRadius: 1,
                          justifyContent: isCollapsed ? 'center' : 'flex-start',
                          px: isCollapsed ? 1.5 : 2,
                          '&:hover': {
                            bgcolor: 'var(--bg-table-hover)'
                          },
                          '&.Mui-selected': {
                            bgcolor: 'var(--bg-sidebar-active)',
                            '&:hover': {
                              bgcolor: 'var(--bg-sidebar-active)'
                            }
                          }
                        }}
                        selected={isChildActive(item.children)}
                      >
                        {isCollapsed && countFor(item) > 0 && <CountBadge n={countFor(item)} dot />}
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 44, justifyContent: 'center' }}>
                          <StyledIcon
                            icon={item.icon}
                            styleKey={item.styleKey}
                            isActive={isChildActive(item.children)}
                            isDark={isDark}
                          />
                        </ListItemIcon>
                        {!isCollapsed && (
                          <>
                            <ListItemText
                              primary={item.text}
                              primaryTypographyProps={{
                                fontWeight: isChildActive(item.children) ? 600 : 500,
                                fontSize: '0.84375rem'
                              }}
                            />
                            {countFor(item) > 0 && !isGroupOpen(item) && <CountBadge n={countFor(item)} />}
                            {isGroupOpen(item) ? <ExpandLess /> : <ExpandMore />}
                          </>
                        )}
                      </ListItemButton>
                    </MenuItemWrapper>
                  </ListItem>
                  {!isCollapsed && (
                    <Collapse in={isGroupOpen(item)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => {
                          const isChildPathActive = location.pathname.startsWith(child.path);
                          return (
                            <ListItem key={child.text} disablePadding sx={{ mb: 0.5 }}>
                              <ListItemButton
                                sx={{
                                  pl: 3,
                                  borderRadius: 1,
                                  '&:hover': {
                                    bgcolor: 'var(--bg-table-hover)'
                                  },
                                  '&.Mui-selected': {
                                    bgcolor: 'var(--bg-sidebar-active)',
                                    '&:hover': {
                                      bgcolor: 'var(--bg-sidebar-active)'
                                    }
                                  }
                                }}
                                selected={isChildPathActive}
                                onClick={() => {
                                  navigate(child.path);
                                  setMobileOpen(false);
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                  <StyledIcon
                                    icon={child.icon}
                                    styleKey={child.styleKey}
                                    isActive={isChildPathActive}
                                    isDark={isDark}
                                    isSubItem
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.text}
                                  primaryTypographyProps={{
                                    fontWeight: isChildPathActive ? 600 : 400,
                                    fontSize: '0.84375rem'
                                  }}
                                />
                                {countFor(child) > 0 && <CountBadge n={countFor(child)} />}
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </>
              ) : (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <MenuItemWrapper title={item.text}>
                    <ListItemButton
                      sx={{
                        borderRadius: 1,
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        px: isCollapsed ? 1.5 : 2,
                        '&:hover': {
                          bgcolor: 'var(--bg-table-hover)'
                        },
                        '&.Mui-selected': {
                          bgcolor: 'var(--bg-sidebar-active)',
                          '&:hover': {
                            bgcolor: 'var(--bg-sidebar-active)'
                          }
                        }
                      }}
                      selected={isItemActive(item)}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 44, justifyContent: 'center' }}>
                        <StyledIcon
                          icon={item.icon}
                          styleKey={item.styleKey}
                          isActive={isItemActive(item)}
                          isDark={isDark}
                        />
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontWeight: isItemActive(item) ? 600 : 500,
                            fontSize: '0.84375rem'
                          }}
                        />
                      )}
                      {countFor(item) > 0 && <CountBadge n={countFor(item)} dot={isCollapsed} />}
                    </ListItemButton>
                  </MenuItemWrapper>
                </ListItem>
              )}
            </React.Fragment>
          );
        })}
      </List>

    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          // Explicit top: 0 is a safety anchor. The AppBar height is driven
          // by the inner Toolbar's `minHeight: 64px !important` (matching the
          // Drawer's Toolbar override, which has always had this rule). An
          // earlier attempt set `height: 64` here too, but that conflicts
          // with border-box + a 1px borderBottom: the AppBar's content area
          // becomes 63px, the 64px-minHeight Toolbar overflows by 1px, and
          // on some browsers this pushes the whole bar into an inconsistent
          // painted height between docked / undocked states. Letting the
          // Toolbar drive the AppBar's intrinsic height (64 + 1px border =
          // 65 rendered) is the symmetric-with-Drawer approach and removes
          // the conflict.
          top: 0,
          // Explicit `right` override: MUI's default for position:fixed AppBar
          // is `right: 0`, which anchors the right edge to the viewport. When
          // chat docks, our `width` calc subtracts dockedWidth but the default
          // right:0 keeps the right edge glued to the viewport — the CSS spec
          // then resolves `left` from `container - ml - width - right`, which
          // pushes the visible AppBar ~360px to the right of where it should
          // be. By explicitly matching `right` to the chat panel's footprint
          // (mirrors the banner pattern at lines ~646 and ~691 which never had
          // this bug), the right edge anchors correctly to `100% - dockedWidth`
          // and the ml+width formula resolves as intended.
          right: isChatDocked ? dockedWidthVar : 0,
          width: {
            sm: isChatDocked
              ? `calc(100% - ${drawerWidth}px - ${dockedWidthVar})`
              : `calc(100% - ${drawerWidth}px)`
          },
          ml: { sm: `${drawerWidth}px` },
          // Transition kept so docking animates in sync with the Drawer paper
          // (which transitions its own width over 0.2s). Without it the AppBar
          // snaps instantly while the Drawer slides, briefly misaligning the
          // sidebar logo with the AppBar title during dock toggle.
          // During an active panel-resize drag, the CSS var inside the width
          // calc changes ~60×/sec; to stop CSS transitions from chasing each
          // frame and leaving a stale paint on release, ChatAssistant sets
          // `body.kc-resizing` which disables the transition via index.css.
          transition: 'width 0.2s ease-in-out, margin 0.2s ease-in-out',
          backgroundColor: 'var(--bg-card)',
          color: 'var(--text-primary)',
          borderBottom: '1px solid var(--border)'
        }}
      >
        {/* minHeight mirrors the Drawer's Toolbar override (line ~283) so
            both header bars are dimensionally symmetric and neither can
            drop to MUI's 48/56 responsive variants under viewport changes. */}
        <Toolbar sx={{ minHeight: '64px !important' }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap component="div">
              {(() => {
                // Check top-level items first
                const topLevel = menuItems.find((item) => item.path === location.pathname);
                if (topLevel) return topLevel.text;
                // Check children
                for (const item of menuItems) {
                  if (item.children) {
                    const child = item.children.find(c => location.pathname.startsWith(c.path));
                    if (child) return child.title || `${item.text} - ${child.text}`;
                  }
                }
                return 'Kodahfy';
              })()}
            </Typography>
            {/* Item 82 — always know which books you're writing into */}
            {isEntityActive && activeEntity && !isDemoMode && (
              <Chip
                size="small"
                color="secondary"
                variant="outlined"
                label={activeEntity.name}
                sx={{ fontWeight: 600, maxWidth: 220 }}
              />
            )}
          </Box>
          <Tooltip title="Position Calculator (Ctrl+K)">
            <IconButton onClick={() => setCalculatorOpen(true)} sx={{ mr: 1, color: 'var(--text-secondary)' }}>
              <CalculateIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={`Report Currency: ${reportingCurrency}`}>
            <IconButton
              onClick={() => navigate('/settings')}
              sx={{
                mr: 1,
                color: 'var(--text-secondary)',
                '&:hover': {
                  bgcolor: 'var(--teal-dim)'
                }
              }}
            >
              <ReactCountryFlag
                countryCode={getCountryCode(reportingCurrency)}
                svg
                style={{
                  width: '1.5em',
                  height: '1.5em',
                  borderRadius: '2px'
                }}
                title={reportingCurrency}
              />
            </IconButton>
          </Tooltip>
          {/* Always-visible way into the demo portfolio (20 Sep 2026). It lived only
              in the avatar menu and on the getting-started card; the first outside
              tester could not find it, and dismissing the card removed the button
              for good. Hidden while already in demo mode (the banner has Exit). */}
          {demoAvailable && !isDemoMode && (
            <Button
              size="small"
              variant="outlined"
              onClick={enterDemoMode}
              sx={{
                mr: 1.5, textTransform: 'none', whiteSpace: 'nowrap',
                color: 'var(--teal)', borderColor: 'var(--teal)',
                '&:hover': { borderColor: 'var(--teal)', backgroundColor: 'rgba(0,200,155,0.08)' }
              }}
            >
              View demo portfolio
            </Button>
          )}
          <Tooltip title="Help & Tutorials">
            <IconButton onClick={() => setHelpOpen(true)} sx={{ mr: 1, color: 'var(--text-secondary)' }}>
              <HelpIcon />
            </IconButton>
          </Tooltip>
          <ThemeToggle />
          <IconButton onClick={handleMenuClick} color="inherit">
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: isDemoMode ? '#f59e0b' : 'var(--teal)',
                fontSize: isDemoMode ? '1.25rem' : (user?.avatar ? '1.25rem' : '0.875rem')
              }}
            >
              {isDemoMode ? '\uD83D\uDCCA' : (user?.avatar || user?.display_name?.[0]?.toUpperCase() || user?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U')}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 220 } } }}
          >
            {/* Portfolio Switcher — Item 82: one entry per entity this login
                can act in; the default entity is the pre-entity "My
                Portfolio". Selecting an entry swaps the whole app to that
                entity's books. */}
            {(entities.length > 0 ? entities : [{ id: '__default', name: 'My Portfolio', is_default: true, entity_type: 'personal' }]).map((e) => {
              const isCurrent = !isDemoMode && (e.is_default ? !isEntityActive : activeEntityId === e.id);
              return (
                <MenuItem
                  key={e.id}
                  onClick={() => {
                    handleMenuClose();
                    if (isDemoMode) exitDemoMode();
                    if (!isCurrent) setActiveEntity(e.is_default ? null : e.id);
                  }}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: e.is_default ? 'var(--teal)' : '#8b5cf6', fontSize: '0.75rem' }}>
                      {e.is_default
                        ? (user?.avatar || user?.display_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U')
                        : (e.name?.[0]?.toUpperCase() || 'E')}
                    </Avatar>
                  </ListItemIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{e.name}</Typography>
                    <Typography variant="caption" noWrap sx={{ color: 'var(--text-muted)', display: 'block' }}>
                      {e.is_default ? user?.email : (e.entity_type ? e.entity_type.toUpperCase() : 'Entity')}
                    </Typography>
                  </Box>
                  {/* Item 82 — rename (owner-only, real entities). stopPropagation so
                      the pencil doesn't also switch portfolios. */}
                  {e.is_owner && e.id !== '__default' && (
                    <Tooltip title="Rename">
                      <IconButton
                        size="small"
                        onClick={(ev) => { ev.stopPropagation(); handleMenuClose(); setRenameTarget(e); }}
                        sx={{ ml: 0.5, color: 'var(--text-muted)' }}
                      >
                        <Pencil size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isCurrent && <CheckIcon sx={{ fontSize: 18, color: 'var(--teal)', ml: 1 }} />}
                </MenuItem>
              );
            })}
            {demoAvailable && (
              <MenuItem
                onClick={() => { if (!isDemoMode) { enterDemoMode(); } handleMenuClose(); }}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: '#f59e0b', fontSize: '1rem' }}>{'\uD83D\uDCCA'}</Avatar>
                </ListItemIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{demoDisplayName}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>Sample data</Typography>
                </Box>
                {isDemoMode && <CheckIcon sx={{ fontSize: 18, color: '#f59e0b', ml: 1 }} />}
              </MenuItem>
            )}
            {/* Item 82 — add a portfolio (create fresh or adopt an existing account) */}
            <MenuItem onClick={() => { handleMenuClose(); setEntityDialogOpen(true); }} sx={{ py: 1 }}>
              <ListItemIcon>
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'transparent', border: '1px dashed', borderColor: 'var(--text-muted)', color: 'var(--text-muted)', fontSize: '1rem' }}>+</Avatar>
              </ListItemIcon>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>Add portfolio…</Typography>
            </MenuItem>
            <Divider />
            {/* In-house feedback — no second sign-in (was Fider) */}
            <MenuItem onClick={() => { handleMenuClose(); navigate('/feedback?new=feature'); }}>
              <ListItemIcon><Lightbulb size={18} /></ListItemIcon>
              Suggest a Feature
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/feedback?new=bug'); }}>
              <ListItemIcon><Bug size={18} /></ListItemIcon>
              Report a Bug
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Demo Mode Banner */}
      {isDemoMode && (
        <Box
          sx={{
            position: 'fixed',
            top: 64,
            left: { sm: drawerWidth },
            right: isChatDocked ? dockedWidthVar : 0,
            zIndex: 1099,
            bgcolor: '#f59e0b',
            color: '#000',
            px: 3,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'left 0.2s ease-in-out',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          <Eye size={16} style={{ marginRight: 8 }} />
          Viewing Demo Portfolio — this is sample data, not your portfolio
          <Box
            component="button"
            onClick={exitDemoMode}
            sx={{
              ml: 'auto',
              bgcolor: 'rgba(0,0,0,0.15)',
              border: 'none',
              borderRadius: 1,
              px: 2,
              py: 0.5,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: '#000',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.25)' }
            }}
          >
            Exit Demo
          </Box>
        </Box>
      )}

      {/* Email Verification Banner */}
      {showVerificationBanner && (
        <Box
          sx={{
            position: 'fixed',
            top: 64,
            left: { sm: drawerWidth },
            right: isChatDocked ? dockedWidthVar : 0,
            zIndex: 1099,
            bgcolor: '#0288d1',
            color: '#fff',
            px: 3,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'left 0.2s ease-in-out',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          Please verify your email address.
          {resendSuccess ? (
            <Box component="span" sx={{ ml: 2, fontWeight: 600 }}>
              Verification email sent!
            </Box>
          ) : (
            <Box
              component="button"
              onClick={handleResendVerification}
              disabled={resendLoading}
              sx={{
                ml: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: 1,
                px: 2,
                py: 0.5,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                '&:disabled': { opacity: 0.6, cursor: 'default' }
              }}
            >
              {resendLoading ? 'Sending...' : 'Resend email'}
            </Box>
          )}
          <Box
            component="button"
            onClick={() => setVerificationBannerDismissed(true)}
            sx={{
              ml: 'auto',
              bgcolor: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: 1,
              px: 2,
              py: 0.5,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            Dismiss
          </Box>
        </Box>
      )}

      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.2s ease-in-out'
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidthExpanded,
              backgroundColor: 'var(--bg-sidebar)',
              borderRight: '1px solid var(--border)'
            }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              transition: 'width 0.2s ease-in-out',
              overflowX: 'hidden',
              backgroundColor: 'var(--bg-sidebar)',
              borderRight: '1px solid var(--border)'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: {
            sm: isChatDocked
              ? `calc(100% - ${drawerWidth}px - ${dockedWidthVar})`
              : `calc(100% - ${drawerWidth}px)`
          },
          mr: isChatDocked ? dockedWidthVar : 0,
          mt: isDemoMode ? 12 : showVerificationBanner ? 12 : 8,
          backgroundColor: 'var(--bg-page)',
          minHeight: '100vh',
          // Transition kept so main-content follows the Drawer's width
          // animation smoothly on dock toggle. Suppressed during drag via
          // `body.kc-resizing` in index.css — see ChatAssistant drag handlers.
          transition: 'width 0.2s ease-in-out, margin 0.2s ease-in-out'
        }}
      >
        {/* Pages are separate chunks (24 Sep 2026): the spinner shows here, inside
            the shell, while a page downloads the first time; the shell stays put. */}
        <ChunkErrorBoundary>
          <Suspense fallback={<PageFallback />}>
            <Outlet key={isDemoMode ? 'demo' : 'live'} />
          </Suspense>
        </ChunkErrorBoundary>
      </Box>

      {/* Position Calculator Modal */}
      <PositionCalculator
        open={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
      />

      {/* Help & Tutorials hub */}
      <HelpCenter open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Item 82 — add portfolio (create fresh / adopt existing account) */}
      <EntityDialog
        open={entityDialogOpen}
        onClose={() => setEntityDialogOpen(false)}
        onCreated={async (entity) => {
          setEntityDialogOpen(false);
          await refreshEntities();
          // Land the user straight in their new portfolio
          if (entity?.id) setActiveEntity(entity.id);
        }}
      />

      {/* Item 82 — rename a portfolio */}
      <RenameEntityDialog
        open={Boolean(renameTarget)}
        entity={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRenamed={async () => {
          setRenameTarget(null);
          await refreshEntities();
        }}
      />

      {/* AI Chat Assistant */}
      <ChatAssistant />

      {/* Collapsed-rail submenu flyout (see railFlyout state above). Rendered
          ONCE at the layout root — drawerContent is mounted by BOTH the
          mobile and desktop drawers, so a Menu inside it opens twice. */}
      <Menu
        open={Boolean(railFlyout)}
        anchorReference="anchorPosition"
        anchorPosition={railFlyout?.pos}
        onClose={() => setRailFlyout(null)}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 200, border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.10)', borderRadius: 2 } } }}
        MenuListProps={{ dense: true, sx: { py: 0.5 } }}
      >
        <Typography component="li" sx={{ px: 2, pt: 0.75, pb: 0.5, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', listStyle: 'none' }}>
          {railFlyout?.item.text}
        </Typography>
        {(railFlyout?.item.children || []).map((child) => {
          const active = location.pathname.startsWith(child.path);
          const Icon = child.icon;
          return (
            <MenuItem
              key={child.text}
              selected={active}
              onClick={() => { setRailFlyout(null); navigate(child.path); }}
              sx={{
                fontSize: '0.84375rem', gap: 1.25, mx: 0.5, borderRadius: 1, py: 0.75,
                color: active ? 'var(--teal)' : 'var(--text-primary)', fontWeight: active ? 600 : 500,
                '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'var(--teal-dim)' }
              }}
            >
              {Icon && <Icon size={16} strokeWidth={1.75} color={active ? 'var(--teal)' : 'var(--text-secondary)'} />}
              {child.text}
              {countFor(child) > 0 && <CountBadge n={countFor(child)} />}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

// Wrap Layout in ChatDockProvider so useChatDock() is available inside Layout
const LayoutWithChatDock = () => (
  <ChatDockProvider>
    <Layout />
  </ChatDockProvider>
);

export default LayoutWithChatDock;
